import { FunctionComponent, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, Stack, Typography, Link } from "@mui/material";

import { useNavigate } from "react-router-dom";

import AppLogo from "@/assets/images/favicon.png";
import { useResetPasswordMutation } from "@/pages/Public/redux/auth.api";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { AuthError } from "@/components";

import { InputField } from "@/components/InputField/InputField";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";

import { ForgotPasswordSchema, ForgotPasswordType } from "./schema/forgot-password";

export const ForgotPassword: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`forgotPassword.${key}`);
	const [resetPassword, { isLoading, isSuccess, error }] = useResetPasswordMutation();

	const navigate = useNavigate();

	const initialValues: ForgotPasswordType = {
		email: "",
	};

	const {
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<ForgotPasswordType>({
		defaultValues: initialValues,
		resolver: zodResolver(ForgotPasswordSchema),
	});

	const [currentFormState, setCurrentFormState] =
		useState<Partial<ForgotPasswordType>>(initialValues);

	const handleSaveFormState = (
		value: ForgotPasswordType[keyof ForgotPasswordType],
		name: keyof ForgotPasswordType
	) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const formErrors = Object.values(errors).map((error) => error.message) as ERROR_TYPE[];

	const onSubmit = async (values: ForgotPasswordType) => {
		try {
			await resetPassword(values).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="w-full flex flex-col items-center">
			<div className="w-full z-10 flex flex-col justify-center items-center mb-8">
				<img alt="logo" className="w-20 h-20" src={AppLogo} />
				<div className="mb-2 text-md">{ts("title")}</div>
				<div className="text-sm mb-2">{ts("weWillEmailYouALinkToResetYourPassword")}</div>
			</div>
			<Card
				elevation={16}
				sx={{
					mb: 4,
					position: "relative",
					width: "100%",
					maxWidth: "444px",
				}}
			>
				<CardContent>
					<Stack>
						<Typography mb={2} variant="h6">
							{ts("resetYourPassword")}
						</Typography>

						{isSuccess ? (
							<Stack>
								<Typography component="p" mb={2} variant="body2">
									{ts("submittedFeedbackMessage")}
								</Typography>
								<Link
									component="a"
									sx={{ mt: 2, textAlign: "center", cursor: "pointer" }}
									underline="hover"
									variant="subtitle2"
									onClick={() => navigate(ROUTE_CONFIG.LOGIN)}
								>
									{ts("backToLogin")}
								</Link>
							</Stack>
						) : (
							<Box component="form" onSubmit={handleSubmit(onSubmit)}>
								<InputField
									error={!!errors.email?.message}
									handleChange={(event) => handleSaveFormState(event.target.value, "email")}
									name="email"
									placeholder={t("basics.email")}
									type="email"
									value={currentFormState.email || ""}
								/>

								<Stack>
									<Button
										className="w-full"
										isLoading={isLoading}
										size={ButtonSize.L}
										title={ts("forgotPasswordCta")}
										type="submit"
									/>

									<Link
										component="a"
										sx={{ mt: 2, textAlign: "center", cursor: "pointer" }}
										underline="hover"
										variant="subtitle2"
										onClick={() => navigate(ROUTE_CONFIG.LOGIN)}
									>
										{ts("backToLogin")}
									</Link>
								</Stack>
							</Box>
						)}

						{getAllErrors(error, formErrors).length ? (
							<AuthError>{renderErrorMessages(getAllErrors(error, formErrors))}</AuthError>
						) : null}
					</Stack>
				</CardContent>
			</Card>
		</div>
	);
};
